import type { PlanMarket, RunStageEntryBase } from '@/types/plan'
import { RUN_TIMED_OUT_ERROR_CODE, type SetupRun } from '@/types/setup-run'

export const ARTICLE_RUN_STAGES = [
  'sources',
  'scope',
  'trace',
  'evidence',
  'gain',
  'argument',
  'plan',
  'draft',
  'edit',
  'check',
  'handoff',
] as const

export type ArticleRunStage = (typeof ARTICLE_RUN_STAGES)[number]

export interface SourcesCounts {
  readonly keywords: number
  readonly serps: number
  readonly organicUrls: number
  readonly aiCitedUrls: number
  readonly read: number
  readonly readable: number
  readonly statements: number
}

export interface ScopeCounts {
  readonly items: number
  readonly points: number
  readonly removedQuotes: number
  readonly droppedPoints: number
  readonly format: string | null
}

export interface TraceCounts {
  readonly facts: number
  readonly traced: number
  readonly bySearch: number
  readonly waiting: number
  readonly judges: number
  readonly searches: number
}

export interface EvidenceCounts {
  readonly ownPages: number
  readonly evidence: number
  readonly itemsWithEvidence: number
  readonly unused: number
}

export interface GainCounts {
  readonly consensus: number
  readonly gains: number
  readonly dropped: number
  readonly questions: number
}

export interface ArgumentCounts {
  readonly candidates: number
  readonly chosen: boolean
}

/** The counts of the stages the port has not built yet, kept as the server writes them. */
export type LaterCounts = Readonly<Record<string, number | string | boolean | null>>

/** The counts of every stage, by stage. */
export interface ArticleCountsByStage {
  readonly sources: SourcesCounts
  readonly scope: ScopeCounts
  readonly trace: TraceCounts
  readonly evidence: EvidenceCounts
  readonly gain: GainCounts
  readonly argument: ArgumentCounts
  readonly plan: LaterCounts
  readonly draft: LaterCounts
  readonly edit: LaterCounts
  readonly check: LaterCounts
  readonly handoff: LaterCounts
}

/** One stage of an article run, its counts typed by the stage. */
export type ArticleRunStageEntry = {
  [S in ArticleRunStage]: RunStageEntryBase & {
    readonly id: S
    readonly counts: ArticleCountsByStage[S]
  }
}[ArticleRunStage]

/** One question the run asks the company, with its answer once given. */
export interface ArticleQuestion {
  readonly id: string
  readonly itemId: string | null
  readonly item: string
  readonly section: string | null
  readonly ask: string
  readonly why: string
  readonly hint: string
  readonly without: string
  readonly answer: string | null
  readonly skipped: boolean
  readonly at: string | null
}

export const GAIN_KINDS = ['disagreement', 'gap', 'ai-answers', 'brand'] as const

export type GainKind = (typeof GAIN_KINDS)[number]

/** What the page can add that the ranking pages do not give, on the quotes it stands on. */
export interface Gain {
  readonly id: string
  readonly itemId: string | null
  readonly kind: GainKind
  readonly text: string
  readonly quotes: readonly { readonly source: string; readonly quote: string }[]
  readonly why: string
}

/** One piece of evidence the argument stands on. */
export interface ArgumentProof {
  readonly id: string
  readonly text: string
  readonly by: string
  readonly url: string | null
}

/** The belief the page argues, the item that carries it and the proof; or no belief, with why. */
export interface ArticleArgument {
  readonly source: 'plan' | 'stage'
  readonly belief: {
    readonly slug: string
    readonly claim: string
    readonly objection: string
  } | null
  readonly item: { readonly id: string; readonly item: string } | null
  readonly proof: readonly ArgumentProof[]
  readonly why: string | null
}

export const ARTICLE_RUN_ERROR_CODES = [
  'article_run_enqueue_failed',
  'article_run_in_progress',
  'article_run_failed',
  'article_raw_store_failed',
  'article_stage_deadline_exceeded',
  'article_page_unknown',
  'article_no_plan_run',
  'article_no_sources',
  'article_no_scope',
  'brand_facts_missing',
  'brand_market_undecided',
  'model_output_invalid',
  'vendor_budget_exhausted',
  'vendor_not_configured',
  'vendor_http_error',
  'vendor_envelope_error',
  'web_content_fetch_failed',
  RUN_TIMED_OUT_ERROR_CODE,
] as const

export type ArticleRunErrorCode = (typeof ARTICLE_RUN_ERROR_CODES)[number]

export const ARTICLE_RUN_STALE_AFTER_MS = 15 * 60 * 1000

/** One article run, `articleRuns/{runId}`; one live run per page. */
export interface ArticleRun extends SetupRun<ArticleRunStage, ArticleRunStageEntry> {
  readonly errorCode?: ArticleRunErrorCode
  readonly pageId: string
  readonly topicId: string
  readonly postId: string | null
  readonly startedByUserId: string | null
  readonly market: PlanMarket
  readonly questions: readonly ArticleQuestion[]
  readonly gains: readonly Gain[] | null
  readonly argument: ArticleArgument | null
}

/** The questions nobody has answered or skipped yet. */
export function openQuestions(questions: readonly ArticleQuestion[]): ArticleQuestion[] {
  return questions.filter((question) => question.answer === null && !question.skipped)
}

/** The counts of one stage of a run, or `null` before it reports. */
export function stageCountsOf<S extends ArticleRunStage>(
  run: Pick<ArticleRun, 'entries'>,
  stage: S,
): ArticleCountsByStage[S] | null {
  const entry = run.entries.find((candidate) => candidate.id === stage)
  return entry && entry.step !== 'queued' ? (entry.counts as ArticleCountsByStage[S]) : null
}
